import i18n from 'locales/i18n';
import { Trans } from 'react-i18next';
import {
	BoldVerticalBarChartData,
	BoldVerticalBarChartProps,
} from '@components/report/chart/bold-vertical-bar-chart/BoldVerticalBarChart';
import { formatBoldVerticalChartData } from '@components/report/chart/utils';
import { VerticalBarAverageChartProps } from '@components/report/chart/vertical-bar-average-chart/VerticalBarAverageChart';

import { TTReportRes } from '@apis/orval/model';

import round, { roundFloat } from '@utils/round';

import { EngagementTtProps } from './EngagementTt';

export class AverageViewsAdapter {
	value: TTReportRes;

	constructor(obj: TTReportRes) {
		this.value = obj;
	}

	get avgViews() {
		return this.value?.engagement?.avg_view ?? undefined;
	}

	adapt(): VerticalBarAverageChartProps {
		return {
			desc: (
				<>
					{/* 평균 조회 수는 <span>{this.avgViews ? round(this.avgViews, 'down') : '-'} 회</span>입니다. */}
					<Trans
						// t={i18n?.t}
						i18nKey={
							'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-views.description'
						}
						components={[
							<span key={'likes'}>
								{i18n?.t('unit.times', {
									number: this.avgViews ? round(this.avgViews, 'down') : '-',
								})}
							</span>,
						]}
					/>
				</>
			),
			barChartValues: [70, 60, 40, 50, 30, 55, 65],
			value: this.avgViews ?? undefined,
			isPositive: true,
		};
	}
}

export class AverageLikesAdapter {
	value: TTReportRes;

	constructor(obj: TTReportRes) {
		this.value = obj;
	}

	get avgLikes() {
		return this.value?.engagement?.avg_like ?? undefined;
	}

	adapt(): VerticalBarAverageChartProps {
		return {
			desc: (
				<>
					{/* 평균 좋아요 수는 <span>{this.avgLikes ? round(this.avgLikes, 'down') : '-'} 개</span>입니다. */}
					<Trans
						// t={i18n?.t}
						i18nKey={
							'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-likes.description'
						}
						components={[
							<span key={'likes'}>
								{i18n?.t('unit.counts', {
									number: this.avgLikes ? round(this.avgLikes, 'down') : '-',
								})}
							</span>,
						]}
					/>
				</>
			),
			barChartValues: [60, 80, 70, 50, 40, 60, 75],
			value: this.avgLikes ?? undefined,
			isPositive: true,
		};
	}
}

export class AverageCommentsAdapter {
	value: TTReportRes;

	constructor(obj: TTReportRes) {
		this.value = obj;
	}

	get avgComments() {
		return this.value?.engagement?.avg_comment ?? undefined;
	}

	adapt(): VerticalBarAverageChartProps {
		return {
			desc: (
				<>
					{/* 평균 댓글 수는 <span>{this.avgComments ? round(this.avgComments, 'down') : '-'} 개</span>입니다. */}
					<Trans
						// t={i18n?.t}
						i18nKey={
							'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-commnets.description'
						}
						components={[
							<span key={'likes'}>
								{i18n?.t('unit.counts', {
									number: this.avgComments ? round(this.avgComments, 'down') : '-',
								})}
							</span>,
						]}
					/>
				</>
			),
			barChartValues: [30, 50, 35, 75, 80, 55, 40],
			value: this.avgComments ?? undefined,
			isPositive: true,
		};
	}
}

export class AverageSharesAdapter {
	value: TTReportRes;

	constructor(obj: TTReportRes) {
		this.value = obj;
	}

	get avgShares() {
		return this.value?.engagement?.avg_share ?? undefined;
	}

	adapt(): VerticalBarAverageChartProps {
		return {
			desc: (
				<>
					{/* 평균 공유 수는 <span>{this.avgShares ? round(this.avgShares, 'down') : '-'} 회</span>입니다. */}
					<Trans
						// t={i18n?.t}
						i18nKey={
							'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-shares.description'
						}
						components={[
							<span key={'likes'}>
								{i18n?.t('unit.times', {
									number: this.avgShares ? round(this.avgShares, 'down') : '-',
								})}
							</span>,
						]}
					/>
				</>
			),
			barChartValues: [20, 40, 30, 65, 50, 60, 45],
			value: this.avgShares ?? undefined,
			isPositive: true,
		};
	}
}

export class AverageSavesAdapter {
	value: TTReportRes;

	constructor(obj: TTReportRes) {
		this.value = obj;
	}

	get avgSaves() {
		return this.value?.engagement?.avg_save ?? undefined;
	}

	adapt(): VerticalBarAverageChartProps {
		return {
			desc: (
				<>
					{/* 평균 저장 수는 <span>{this.avgSaves ? round(this.avgSaves, 'down') : '-'} 회</span>입니다. */}
					<Trans
						// t={i18n?.t}
						i18nKey={
							'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-saves.description'
						}
						components={[
							<span key={'likes'}>
								{i18n?.t('unit.times', {
									number: this.avgSaves ? round(this.avgSaves, 'down') : '-',
								})}
							</span>,
						]}
					/>
				</>
			),
			barChartValues: [70, 50, 60, 45, 30, 35, 50],
			value: this.avgSaves ?? undefined,
			isPositive: true,
		};
	}
}

export class EngagementRateAdapter {
	value: TTReportRes;

	constructor(obj: TTReportRes) {
		this.value = obj;
	}

	/**
	 * @description 참여율(ER)
	 */
	get realEngagement() {
		return this.value?.engagement?.real_engagement ?? undefined;
	}
	/**
	 * @description 유사 그룹 평균 참여율(ER)
	 */
	get avgRealEngagement() {
		return this.value?.engagement?.avg_similar_group_real_engagement ?? undefined;
	}

	/**
	 * @description 참여율(ER) 비교 결과 텍스트
	 */
	textMapper = {
		high: (
			<Trans
				// t={i18n?.t}
				i18nKey={
					'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-real-engagement.high-comparison'
				}
				components={[
					<span key="high">
						{i18n?.t(
							'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-real-engagement.high',
						)}
					</span>,
				]}
			/>
		),
		normal: (
			<Trans
				// t={i18n?.t}
				i18nKey={
					'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-real-engagement.normal-comparison'
				}
				components={[
					<span key="high">
						{i18n?.t(
							'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-real-engagement.normal',
						)}
					</span>,
				]}
			/>
		),
		low: (
			<Trans
				// t={i18n?.t}
				i18nKey={
					'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-real-engagement.low-comparison'
				}
				components={[
					<span key="high">
						{i18n?.t(
							'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-real-engagement.low',
						)}
					</span>,
				]}
			/>
		),
		unmeasurable: (
			<Trans
				// t={i18n?.t}
				i18nKey={
					'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-real-engagement.unmeasurable-comparison'
				}
				components={[
					<span key="high">
						{i18n?.t(
							'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-real-engagement.unmeasurable',
						)}
					</span>,
				]}
			/>
		),
	} as const;

	/**
	 * @description 참여율(ER) 비교 결과 상태
	 */
	get status() {
		if (this.realEngagement === undefined) return 'unmeasurable';
		if (this.avgRealEngagement === undefined) return 'unmeasurable';

		return this.realEngagement > this.avgRealEngagement * 1.1
			? 'high'
			: this.realEngagement < this.avgRealEngagement * 0.9
			? 'low'
			: 'normal';
	}

	get infAdvancedData(): BoldVerticalBarChartData {
		const hasAllValue = this.realEngagement && this.avgRealEngagement;

		return {
			value: `${roundFloat(this.realEngagement ?? 0, 'down', 2)}%`,
			label: this.value?.account?.profile?.username ?? '',
			valuePercentage: hasAllValue
				? (this.realEngagement / (this.realEngagement + this.avgRealEngagement)) * 100
				: 0,
			colorType: this.status === 'high' ? 'primary' : 'black',
			isMain: true,
		};
	}

	get similarGroupAdvancedData(): BoldVerticalBarChartData {
		const hasAllValue = this.realEngagement && this.avgRealEngagement;

		return {
			value: `${roundFloat(this.avgRealEngagement ?? 0, 'down', 2)}%`,
			label: i18n?.t(
				'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-real-engagement.similar-group',
			), //'유사 그룹 평균',
			valuePercentage: hasAllValue
				? (this.avgRealEngagement / (this.realEngagement + this.avgRealEngagement)) * 100
				: 0,
			colorType: 'gray',
		};
	}

	get data(): BoldVerticalBarChartData[] {
		const realEngagementDefaultData = {
			label: this.value?.account?.profile?.username ?? '',
		};
		const avgRealEngagementDefaultData = {
			label: i18n?.t(
				'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.avg-real-engagement.similar-group',
			),
		};

		const realEngagementData = formatBoldVerticalChartData(
			this.realEngagement,
			realEngagementDefaultData,
			this.infAdvancedData,
		);

		const avgRealEngagementData = formatBoldVerticalChartData(
			this.avgRealEngagement,
			avgRealEngagementDefaultData,
			this.similarGroupAdvancedData,
		);

		return [realEngagementData, avgRealEngagementData];
	}

	get valueText() {
		if (!this.realEngagement)
			return (
				i18n?.t(
					'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.real-engagement.preparing',
				) ?? ''
			);

		return (
			i18n?.t(
				'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.real-engagement.description2',
				{ realEngagement: roundFloat(this.realEngagement, 'down', 2).toString() },
			) ?? ''
		);
	}

	adapt(): BoldVerticalBarChartProps {
		if (this.realEngagement === undefined) return { isError: true };

		return {
			desc: (
				<Trans
					// t={i18n?.t}
					i18nKey={
						'infReportTiktok:features.report.section-content.tt.engagement-tt.engagement-tt-adapter.real-engagement.description1'
					}
					components={[
						<span key="realEngagement">{this.valueText}</span>,
						this.textMapper[this.status],
					]}
				/>
			),
			data: this.data,
		};
	}
}

export class EngagementTtAdapter {
	value: TTReportRes;

	constructor(obj: TTReportRes) {
		this.value = obj;
	}

	adapt(): EngagementTtProps {
		const averageViews = new AverageViewsAdapter(this.value).adapt();
		const averageLikes = new AverageLikesAdapter(this.value).adapt();
		const averageComments = new AverageCommentsAdapter(this.value).adapt();
		const averageShares = new AverageSharesAdapter(this.value).adapt();
		const averageSaves = new AverageSavesAdapter(this.value).adapt();
		const engagementRate = new EngagementRateAdapter(this.value).adapt();

		return {
			averageViews,
			averageLikes,
			averageComments,
			averageShares,
			averageSaves,
			engagementRate,
		};
	}
}
